import { useState, useCallback, useEffect, useRef } from 'react';
import { parseTimeToMinutes, parseDurationToMinutes } from './useSimClock';
import { CEO_BRIEFING } from '../data/ceoBriefing';
import { INCOMING_EMAILS } from '../data/emailReplyData';

const MEETING_FOLLOW_UPS = {
  m1: {
    notes: 'Aligned on Q1 targets: $342K revenue (revised down from $380K). Marketing budget approved pending Meta campaign performance. Engineering hire greenlit — Tali to begin sourcing immediately.',
    task: { id: 'ft-m1', title: 'Distribute Q1 targets to department heads', agent: 'luc', agentNote: 'Generated from meeting notes', urgency: 'due-today', description: 'Luc summarized Q1 Planning Sync decisions. Review and send to Priya, David, Maya, Riya, and Neel.' },
  },
  m2: {
    notes: 'Acme Corp demo received positively. Sarah Kim impressed with workflow automation depth. Pricing stays at $48K/year. Next: send revised proposal with implementation timeline by Wednesday.',
    task: { id: 'ft-m2', title: 'Send revised Acme Corp proposal', agent: 'sally', agentNote: 'Sally will prepare this', urgency: 'time-sensitive', description: 'Sally is updating the proposal with the implementation timeline discussed. Review before sending to Sarah Kim.' },
  },
  m3: {
    notes: 'Sprint velocity stable at 82%. Top priority: v2.3 hotfix for support SLA crisis. Two features deferred to Sprint 15. Team capacity at 92% — new hire critical for Q2.',
    task: { id: 'ft-m3', title: 'Confirm sprint backlog priorities', agent: 'stark', agentNote: 'Stark compiled priorities', urgency: 'due-today', description: "Stark has prioritized the Sprint 15 backlog based on today's review. Confirm the deferred features and hotfix priority." },
  },
};

const INITIAL_STATUSES = {
  approvals: { a1: 'pending', a2: 'pending', a3: 'pending' },
  risks: { r1: 'pending', r2: 'pending' },
  mustDo: { p1: 'pending', p2: 'pending', p3: 'pending' },
  meetings: { m1: 'upcoming', m2: 'upcoming', m3: 'upcoming' },
};

export default function useSimState(totalMinutes, addToast, cycle) {
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  const [statuses, setStatuses] = useState({ ...INITIAL_STATUSES });
  const [notifications, setNotifications] = useState([]);
  const [followUpTasks, setFollowUpTasks] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxStatus, setInboxStatus] = useState({});
  const [arrivedEmails, setArrivedEmails] = useState([]);

  // Priya's local briefing state — resolves ripple to CEO view
  const [priyaResolvedAlerts, setPriyaResolvedAlerts] = useState(new Set());
  const [priyaCompletedTasks, setPriyaCompletedTasks] = useState(new Set());
  const [priyaHandledEmails, setPriyaHandledEmails] = useState({});
  const [priyaCampaignStates, setPriyaCampaignStates] = useState({});
  const [priyaDecisions, setPriyaDecisions] = useState({});
  const [priyaCreatedCampaigns, setPriyaCreatedCampaigns] = useState([]);
  const [priyaCreatedPosts, setPriyaCreatedPosts] = useState({});  // keyed by date
  const [priyaCreatedContent, setPriyaCreatedContent] = useState({ draft: [], review: [], scheduled: [], published: [] });

  // David's local briefing state (no ripple for now, per scope)
  const [davidResolvedAlerts, setDavidResolvedAlerts] = useState(new Set());
  const [davidCompletedTasks, setDavidCompletedTasks] = useState(new Set());
  const [davidHandledEmails, setDavidHandledEmails] = useState({});
  const [davidDealStates, setDavidDealStates] = useState({});
  const [davidDecisions, setDavidDecisions] = useState({});
  const [davidCreatedDeals, setDavidCreatedDeals] = useState([]);
  const [davidCreatedSequences, setDavidCreatedSequences] = useState([]);

  // ═══════════════════════════════════════════════════════════
  // Cross-account task RIPPLES — rippled artifacts routed between accounts.
  // Shape: { arjun: {tasks:[], emails:[], approvals:[], risks:[]}, priya:{...}, david:{...}, maya:{...}, neel:{...}, riya:{...} }
  // ═══════════════════════════════════════════════════════════
  const EMPTY_RIPPLE_BUCKET = { tasks: [], emails: [], approvals: [], risks: [] };
  const EMPTY_RIPPLES = {
    arjun: { ...EMPTY_RIPPLE_BUCKET }, priya: { ...EMPTY_RIPPLE_BUCKET }, david: { ...EMPTY_RIPPLE_BUCKET },
    maya: { ...EMPTY_RIPPLE_BUCKET }, neel: { ...EMPTY_RIPPLE_BUCKET }, riya: { ...EMPTY_RIPPLE_BUCKET },
  };
  const [ripples, setRipples] = useState(EMPTY_RIPPLES);
  // Track completed / handled ripple IDs per account
  const [rippleCompletions, setRippleCompletions] = useState({});

  const firedRef = useRef(new Set());
  const cycleRef = useRef(cycle);

  // Reset everything when cycle changes (clock loops back to 9 AM)
  useEffect(() => {
    if (cycle !== cycleRef.current) {
      cycleRef.current = cycle;
      setStatuses({ ...INITIAL_STATUSES });
      setNotifications([]);
      setFollowUpTasks([]);
      setMeetingNotes({});
      setUnreadCount(0);
      setInboxStatus({});
      setArrivedEmails([]);
      firedRef.current = new Set();
      // Clear all cross-account ripples on cycle reset
      setRipples({
        arjun: { tasks: [], emails: [], approvals: [], risks: [] },
        priya: { tasks: [], emails: [], approvals: [], risks: [] },
        david: { tasks: [], emails: [], approvals: [], risks: [] },
        maya: { tasks: [], emails: [], approvals: [], risks: [] },
        neel: { tasks: [], emails: [], approvals: [], risks: [] },
        riya: { tasks: [], emails: [], approvals: [], risks: [] },
      });
      setRippleCompletions({});
    }
  }, [cycle]);

  const addNotification = useCallback((notification) => {
    const entry = { ...notification, id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, timestamp: Date.now() };
    setNotifications((prev) => [entry, ...prev]);
    setUnreadCount((prev) => prev + 1);
  }, []);

  const markAllRead = useCallback(() => setUnreadCount(0), []);

  // Mark ripple notifications as read for a specific account
  const markRippleNotifsRead = useCallback((account) => {
    if (!account) return;
    setNotifications((prev) => prev.map((n) => {
      if (n.toAccount !== account) return n;
      const readBy = new Set(n.readBy || []);
      readBy.add(account);
      return { ...n, readBy: Array.from(readBy) };
    }));
  }, []);

  // Inbox actions
  const markEmailDone = useCallback((id, action) => {
    setInboxStatus((prev) => ({ ...prev, [id]: action }));
    const label = action === 'sent' ? 'Reply sent' : action === 'drafted' ? 'Saved as draft' : 'Reminder set';
    addToastRef.current?.({ type: 'success', title: label, message: `Email handled` });
    addNotification({ type: 'email-handled', title: `${label}: ${id}`, icon: 'check' });
  }, [addNotification]);

  // Approval actions
  const approveItem = useCallback((id) => {
    setStatuses((prev) => ({ ...prev, approvals: { ...prev.approvals, [id]: 'approved' } }));
    const item = CEO_BRIEFING.approvals.find((a) => a.id === id);
    addToastRef.current?.({ type: 'success', title: 'Approved', message: item?.title || 'Item approved' });
    addNotification({ type: 'approval', title: `Approved: ${item?.title}`, icon: 'check' });
  }, [addNotification]);

  const rejectItem = useCallback((id) => {
    setStatuses((prev) => ({ ...prev, approvals: { ...prev.approvals, [id]: 'rejected' } }));
    const item = CEO_BRIEFING.approvals.find((a) => a.id === id);
    addToastRef.current?.({ type: 'warning', title: 'Rejected', message: item?.title || 'Item rejected' });
    addNotification({ type: 'rejection', title: `Rejected: ${item?.title}`, icon: 'x' });
  }, [addNotification]);

  const resolveRisk = useCallback((id) => {
    setStatuses((prev) => ({ ...prev, risks: { ...prev.risks, [id]: 'resolved' } }));
    const item = CEO_BRIEFING.risks.find((r) => r.id === id);
    addToastRef.current?.({ type: 'success', title: 'Risk resolved', message: item?.title || 'Risk resolved' });
    addNotification({ type: 'risk-resolved', title: `Resolved: ${item?.title}`, icon: 'shield' });
  }, [addNotification]);

  const startTask = useCallback((id) => {
    setStatuses((prev) => ({ ...prev, mustDo: { ...prev.mustDo, [id]: 'in-progress' } }));
  }, []);

  const completeTask = useCallback((id) => {
    setStatuses((prev) => ({ ...prev, mustDo: { ...prev.mustDo, [id]: 'completed' } }));
    const item = CEO_BRIEFING.priorities.mustDo.find((t) => t.id === id);
    addToastRef.current?.({ type: 'success', title: 'Task completed', message: item?.title || 'Task done' });
    addNotification({ type: 'task-completed', title: `Completed: ${item?.title}`, icon: 'check' });
  }, [addNotification]);

  // ═══════════════════════════════════════════════════════════
  // Priya's briefing actions — ripple to CEO where relevant
  // ═══════════════════════════════════════════════════════════
  const markPriyaEmailDone = useCallback((id, action) => {
    setPriyaHandledEmails((prev) => ({ ...prev, [id]: action }));
    const label = action === 'sent' ? 'Reply sent' : action === 'drafted' ? 'Saved as draft' : 'Reminder set';
    addToastRef.current?.({ type: 'success', title: label, message: 'Email handled' });
  }, []);

  const completePriyaTask = useCallback((id) => {
    setPriyaCompletedTasks((prev) => { const next = new Set(prev); next.add(id); return next; });
    addToastRef.current?.({ type: 'success', title: 'Task completed', message: 'Task done' });
  }, []);

  const resolvePriyaAlert = useCallback((id) => {
    setPriyaResolvedAlerts((prev) => { const next = new Set(prev); next.add(id); return next; });
    // Ripple: la1 (Priya's lead quality alert) also resolves Arjun's r1 (same issue)
    if (id === 'la1') {
      setStatuses((prev) => ({ ...prev, risks: { ...prev.risks, r1: 'resolved' } }));
      addToastRef.current?.({ type: 'success', title: 'Lead quality alert resolved', message: 'Also reflected on CEO view' });
    } else {
      addToastRef.current?.({ type: 'success', title: 'Alert resolved', message: 'Handled' });
    }
  }, []);

  const setPriyaCampaignState = useCallback((id, state) => {
    setPriyaCampaignStates((prev) => ({ ...prev, [id]: state }));
    addToastRef.current?.({ type: 'success', title: state === 'optimized' ? 'Campaign optimized' : 'Campaign launched', message: `${id} updated` });
  }, []);

  const setPriyaDecision = useCallback((id, state) => {
    setPriyaDecisions((prev) => ({ ...prev, [id]: state }));
    addToastRef.current?.({ type: state === 'approved' ? 'success' : 'warning', title: state === 'approved' ? 'Approved' : 'Rejected', message: `Decision ${id} ${state}` });
  }, []);

  const createPriyaCampaign = useCallback((campaign) => {
    setPriyaCreatedCampaigns((prev) => {
      if (prev.some((c) => c.id === campaign.id)) return prev;
      return [campaign, ...prev];
    });
    addToastRef.current?.({ type: 'success', title: 'Campaign created', message: `${campaign.name} added to your dashboard` });
  }, []);

  const createPriyaPost = useCallback((post, dateKey) => {
    if (!dateKey) return;
    setPriyaCreatedPosts((prev) => {
      const existing = prev[dateKey] || [];
      if (existing.some((p) => p.id === post.id)) return prev;
      return { ...prev, [dateKey]: [post, ...existing] };
    });
    addToastRef.current?.({ type: 'success', title: 'Post scheduled', message: `${post.title} queued for ${dateKey}` });
  }, []);

  const createPriyaContent = useCallback((content, stage = 'review') => {
    if (!content) return;
    setPriyaCreatedContent((prev) => {
      const existing = prev[stage] || [];
      if (existing.some((c) => c.id === content.id)) return prev;
      return { ...prev, [stage]: [content, ...existing] };
    });
    addToastRef.current?.({ type: 'success', title: 'Content drafted', message: `${content.title} delivered to ${stage === 'review' ? 'Review' : stage}` });
  }, []);

  const priyaState = {
    resolvedAlerts: priyaResolvedAlerts,
    completedTasks: priyaCompletedTasks,
    handledEmails: priyaHandledEmails,
    campaignStates: priyaCampaignStates,
    decisions: priyaDecisions,
    createdCampaigns: priyaCreatedCampaigns,
    createdPosts: priyaCreatedPosts,
    createdContent: priyaCreatedContent,
    markPriyaEmailDone, completePriyaTask, resolvePriyaAlert, setPriyaCampaignState, setPriyaDecision, createPriyaCampaign, createPriyaPost, createPriyaContent,
  };

  // ═══════════════════════════════════════════════════════════
  // David's briefing actions (no CEO ripple for now)
  // ═══════════════════════════════════════════════════════════
  const markDavidEmailDone = useCallback((id, action) => {
    setDavidHandledEmails((prev) => ({ ...prev, [id]: action }));
    const label = action === 'sent' ? 'Reply sent' : action === 'drafted' ? 'Saved as draft' : 'Reminder set';
    addToastRef.current?.({ type: 'success', title: label, message: 'Email handled' });
  }, []);

  const completeDavidTask = useCallback((id) => {
    setDavidCompletedTasks((prev) => { const next = new Set(prev); next.add(id); return next; });
    addToastRef.current?.({ type: 'success', title: 'Task completed', message: 'Task done' });
  }, []);

  const resolveDavidAlert = useCallback((id) => {
    setDavidResolvedAlerts((prev) => { const next = new Set(prev); next.add(id); return next; });
    addToastRef.current?.({ type: 'success', title: 'Pipeline alert resolved', message: 'Q1 recalibration plan live' });
  }, []);

  const setDavidDealState = useCallback((id, state) => {
    setDavidDealStates((prev) => ({ ...prev, [id]: state }));
    addToastRef.current?.({ type: 'success', title: 'Deal updated', message: `${id} → ${state}` });
  }, []);

  const setDavidDecision = useCallback((id, state) => {
    setDavidDecisions((prev) => ({ ...prev, [id]: state }));
    addToastRef.current?.({ type: state === 'approved' ? 'success' : 'warning', title: state === 'approved' ? 'Approved' : 'Rejected', message: `Decision ${id} ${state}` });
  }, []);

  const createDavidDeal = useCallback((deal) => {
    if (!deal) return;
    setDavidCreatedDeals((prev) => {
      if (prev.some((d) => d.id === deal.id)) return prev;
      return [deal, ...prev];
    });
    addToastRef.current?.({ type: 'success', title: 'Deal added', message: `${deal.name} added to Prospecting` });
  }, []);

  const createDavidSequence = useCallback((seq) => {
    if (!seq) return;
    setDavidCreatedSequences((prev) => {
      if (prev.some((s) => s.id === seq.id)) return prev;
      return [seq, ...prev];
    });
    addToastRef.current?.({ type: 'success', title: 'Sequence live', message: `${seq.name} activated` });
  }, []);

  const davidState = {
    resolvedAlerts: davidResolvedAlerts,
    completedTasks: davidCompletedTasks,
    handledEmails: davidHandledEmails,
    dealStates: davidDealStates,
    decisions: davidDecisions,
    createdDeals: davidCreatedDeals,
    createdSequences: davidCreatedSequences,
    markDavidEmailDone, completeDavidTask, resolveDavidAlert, setDavidDealState, setDavidDecision, createDavidDeal, createDavidSequence,
  };

  // ═══════════════════════════════════════════════════════════
  // Ripple actions — add / acknowledge cross-account artifacts
  // ═══════════════════════════════════════════════════════════
  const ACCOUNT_DISPLAY = { arjun: 'Arjun', priya: 'Priya', david: 'David', maya: 'Maya', neel: 'Neel', riya: 'Riya' };

  const addRipple = useCallback((toAccount, kind, payload, fromAccount = 'arjun') => {
    if (!toAccount || !kind || !payload) return;
    if (!['tasks', 'emails', 'approvals', 'risks'].includes(kind)) return;
    const rippleId = payload.id || `rp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const entry = { ...payload, id: rippleId, from: fromAccount, rippledAt: Date.now() };
    setRipples((prev) => {
      const bucket = prev[toAccount] || { tasks: [], emails: [], approvals: [], risks: [] };
      // dedupe by id
      if (bucket[kind].some((x) => x.id === rippleId)) return prev;
      return { ...prev, [toAccount]: { ...bucket, [kind]: [entry, ...bucket[kind]] } };
    });
    const toName = ACCOUNT_DISPLAY[toAccount] || toAccount;
    const fromName = ACCOUNT_DISPLAY[fromAccount] || fromAccount;
    const label = kind === 'tasks' ? 'Task routed' : kind === 'emails' ? 'Email delivered' : kind === 'approvals' ? 'Approval requested' : 'Risk flagged';
    addToastRef.current?.({ type: 'handoff', title: `${label} to ${toName}`, message: payload.title || payload.subject || 'New item' });
    // Also push into the recipient's notification panel (account-scoped)
    const notifType = kind === 'approvals' ? 'approval' : kind === 'emails' ? 'new-email' : kind === 'risks' ? 'risk-resolved' : 'follow-up';
    const notifIcon = kind === 'approvals' ? 'check' : kind === 'emails' ? 'bell' : kind === 'risks' ? 'shield' : 'arrow';
    addNotification({
      type: notifType,
      title: `From ${fromName}: ${payload.title || payload.subject || label}`,
      icon: notifIcon,
      toAccount,
    });
  }, [addNotification]);

  const completeRippledTask = useCallback((toAccount, id) => {
    setRippleCompletions((prev) => ({ ...prev, [`${toAccount}:${id}`]: 'completed' }));
    setRipples((prev) => {
      const bucket = prev[toAccount];
      if (!bucket) return prev;
      const updated = {};
      for (const k of ['tasks', 'emails', 'approvals', 'risks']) {
        updated[k] = (bucket[k] || []).map((x) => x.id === id ? { ...x, status: 'completed' } : x);
      }
      return { ...prev, [toAccount]: updated };
    });
    // Ack toast routed back to originating account
    const src = Object.values(ripples).flatMap((b) => [...(b?.tasks || []), ...(b?.emails || []), ...(b?.approvals || []), ...(b?.risks || [])]).find((x) => x.id === id);
    const fromName = src?.from ? (ACCOUNT_DISPLAY[src.from] || src.from) : 'Sender';
    addToastRef.current?.({ type: 'success', title: `${fromName} notified`, message: `Action confirmed back to ${fromName}` });
  }, [ripples]);

  const acknowledgeRipple = useCallback((toAccount, id, action = 'handled') => {
    setRippleCompletions((prev) => ({ ...prev, [`${toAccount}:${id}`]: action }));
    setRipples((prev) => {
      const bucket = prev[toAccount];
      if (!bucket) return prev;
      const updated = {};
      for (const k of ['tasks', 'emails', 'approvals', 'risks']) {
        updated[k] = (bucket[k] || []).map((x) => x.id === id ? { ...x, status: action } : x);
      }
      return { ...prev, [toAccount]: updated };
    });
  }, []);

  // Clock-driven events
  useEffect(() => {
    // Meeting events
    CEO_BRIEFING.meetings.forEach((meeting) => {
      const startMin = parseTimeToMinutes(meeting.time);
      const dur = parseDurationToMinutes(meeting.duration);
      const endMin = startMin + dur;
      const reminderMin = startMin - 15;

      if (totalMinutes >= reminderMin && !firedRef.current.has(`reminder-${meeting.id}`)) {
        firedRef.current.add(`reminder-${meeting.id}`);
        addToastRef.current?.({ type: 'info', title: 'Meeting in 15 minutes', message: `${meeting.title} with ${meeting.with}` });
        addNotification({ type: 'meeting-reminder', title: `${meeting.title} starts in 15 min`, icon: 'bell' });
        setStatuses((prev) => ({ ...prev, meetings: { ...prev.meetings, [meeting.id]: 'reminded' } }));
      }
      if (totalMinutes >= startMin && !firedRef.current.has(`start-${meeting.id}`)) {
        firedRef.current.add(`start-${meeting.id}`);
        setStatuses((prev) => ({ ...prev, meetings: { ...prev.meetings, [meeting.id]: 'in-progress' } }));
      }
      if (totalMinutes >= endMin && !firedRef.current.has(`end-${meeting.id}`)) {
        firedRef.current.add(`end-${meeting.id}`);
        setStatuses((prev) => ({ ...prev, meetings: { ...prev.meetings, [meeting.id]: 'completed' } }));
        const followUp = MEETING_FOLLOW_UPS[meeting.id];
        if (followUp) {
          addToastRef.current?.({ type: 'success', title: 'Meeting notes ready', message: `Luc generated notes for ${meeting.title}` });
          addNotification({ type: 'meeting-notes', title: `Notes ready: ${meeting.title}`, icon: 'file' });
          setMeetingNotes((prev) => ({ ...prev, [meeting.id]: followUp.notes }));
          if (followUp.task) {
            setTimeout(() => {
              setFollowUpTasks((prev) => [...prev, followUp.task]);
              addToastRef.current?.({ type: 'handoff', title: 'New follow-up task', message: followUp.task.title });
              addNotification({ type: 'follow-up', title: followUp.task.title, icon: 'arrow' });
            }, 2000);
          }
        }
      }
    });

    // New emails arriving
    INCOMING_EMAILS.forEach((email) => {
      if (totalMinutes >= email.arriveAtMinute && !firedRef.current.has(`email-${email.id}`)) {
        firedRef.current.add(`email-${email.id}`);
        setArrivedEmails((prev) => [...prev, email]);
        addToastRef.current?.({ type: 'info', title: 'New email', message: `From ${email.sender}: ${email.summary.slice(0, 50)}...` });
        addNotification({ type: 'new-email', title: `New email from ${email.sender}`, icon: 'bell' });
      }
    });
  }, [totalMinutes, addNotification]);

  const pendingApprovals = Object.values(statuses.approvals).filter((s) => s === 'pending').length;
  const pendingRisks = Object.values(statuses.risks).filter((s) => s === 'pending').length;
  const pendingTasks = Object.values(statuses.mustDo).filter((s) => s === 'pending' || s === 'in-progress').length + followUpTasks.length;

  // Compute active inbox: original emails + arrived emails, minus handled ones
  const allEmails = [...CEO_BRIEFING.inbox, ...arrivedEmails];
  const pendingEmails = allEmails.filter((e) => !inboxStatus[e.id]);
  const handledEmailCount = Object.keys(inboxStatus).length;

  return {
    statuses, notifications, followUpTasks, meetingNotes, unreadCount, markAllRead, markRippleNotifsRead,
    pendingApprovals, pendingRisks, pendingTasks,
    approveItem, rejectItem, resolveRisk, startTask, completeTask,
    inboxStatus, markEmailDone, allEmails, pendingEmails, handledEmailCount, arrivedEmails,
    priyaState, davidState,
    // Cross-account ripples
    ripples, addRipple, completeRippledTask, acknowledgeRipple,
  };
}
